import { useCallback, useEffect, useState } from "react";
import { callTool } from "../api";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { formatNoteStamp, ratingLineage } from "../ratings";
import type { ContactCountPatch } from "./counts";

export interface ContactActivityRow {
  contact_id: string;
  account_id: string | null;
  booth?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface NoteHit {
  note_id: string;
  body: string | null;
  actor: string;
  created_at: string;
}

interface FollowupHit {
  followup_id: string;
  title: string;
  due_at: string | null;
  status: string;
  channel: string | null;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M12 3.5a3 3 0 0 1 3 3V11a3 3 0 0 1-6 0V6.5a3 3 0 0 1 3-3z"
      />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6.5 11a5.5 5.5 0 0 0 11 0M12 16.5V20" />
    </svg>
  );
}

export function personName(row: ContactActivityRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(" ") || "—";
}

export function NotesPane({
  row,
  actor,
  onSaved,
}: {
  row: ContactActivityRow;
  actor: string;
  onSaved?: (contactId: string, patch: ContactCountPatch) => void;
}) {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<NoteHit[]>([]);
  const appendTranscript = useCallback((text: string) => {
    setBody((current) => {
      const prefix = current.trimEnd();
      return prefix ? `${prefix} ${text}` : text;
    });
  }, []);
  const speech = useSpeechToText(appendTranscript);

  const loadHistory = useCallback(async () => {
    try {
      const data = await callTool<{ results: NoteHit[] }>("note_list", {
        contact_id: row.contact_id,
        limit: 50,
      });
      const listed = data.results ?? [];
      setHistory(listed);
      return listed;
    } catch {
      setHistory([]);
      return [];
    }
  }, [row.contact_id]);

  useEffect(() => {
    let cancelled = false;
    void loadHistory().then((listed) => {
      if (!cancelled) onSaved?.(row.contact_id, { note_count: listed.length });
    });
    return () => {
      cancelled = true;
    };
  }, [loadHistory, row.contact_id, onSaved]);

  async function submit() {
    if (!body.trim()) {
      setError("Write a note before saving.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await callTool("note_add", {
        body: body.trim(),
        contact_id: row.contact_id,
        account_id: row.account_id ?? undefined,
        booth_number: row.booth ?? undefined,
        actor,
        lineage: ratingLineage(),
      });
      setBody("");
      const listed = await loadHistory();
      onSaved?.(row.contact_id, { note_count: listed.length });
    } catch (err) {
      setError(err instanceof Error ? err.message : "note_add failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="contact-activity-pane">
      <label className="field">
        Note
        <div className="note-entry-wrap">
          <textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Booth conversation, next step, context…"
          />
          <button
            type="button"
            className={`note-entry-mic${speech.listening ? " on" : ""}`}
            disabled={!speech.available}
            aria-label={speech.listening ? "Stop microphone" : "Voice to text"}
            title={speech.available ? (speech.listening ? "Stop microphone" : "Voice to text") : "Speech not available in this browser"}
            aria-pressed={speech.listening}
            onClick={speech.toggle}
          >
            <MicIcon />
          </button>
        </div>
      </label>
      <div className="contact-note-actions">
        <button type="button" disabled={busy} onClick={() => void submit()}>
          {busy ? "Saving…" : "Save note"}
        </button>
      </div>
      {!speech.available ? <p className="muted">Speech not available in this browser</p> : null}
      {error ? <p className="err">{error}</p> : null}
      <h3 className="contact-history-heading">History</h3>
      {history.length ? (
        <ol className="note-history">
          {history.map((item) => (
            <li key={item.note_id}>
              <div className="muted">
                {formatNoteStamp(item.created_at)}
                {item.actor ? ` · ${item.actor}` : ""}
              </div>
              <p>{item.body}</p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">No notes for this person yet.</p>
      )}
    </div>
  );
}

export function FollowupsPane({
  row,
  eventKey,
  actor,
  onSaved,
}: {
  row: ContactActivityRow;
  eventKey: string;
  actor: string;
  onSaved?: (contactId: string, patch: ContactCountPatch) => void;
}) {
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [channel, setChannel] = useState("email");
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<FollowupHit[]>([]);

  const loadItems = useCallback(async () => {
    try {
      const data = await callTool<{ results: FollowupHit[] }>("followup_list", {
        event_key: eventKey,
        contact_id: row.contact_id,
        limit: 50,
      });
      const listed = data.results ?? [];
      setItems(listed);
      return listed;
    } catch {
      setItems([]);
      return [];
    }
  }, [eventKey, row.contact_id]);

  useEffect(() => {
    let cancelled = false;
    void loadItems().then((listed) => {
      if (!cancelled) onSaved?.(row.contact_id, { followup_count: listed.length });
    });
    return () => {
      cancelled = true;
    };
  }, [loadItems, row.contact_id, onSaved]);

  async function submit() {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await callTool("followup_upsert", {
        event_key: eventKey,
        contact_id: row.contact_id,
        account_id: row.account_id ?? undefined,
        title: title.trim(),
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        channel,
        status: "open",
        draft_body: draft.trim() || undefined,
        owner: actor,
        actor,
        lineage: ratingLineage(),
      });
      setTitle("");
      setDueAt("");
      setDraft("");
      const listed = await loadItems();
      onSaved?.(row.contact_id, { followup_count: listed.length });
    } catch (err) {
      setError(err instanceof Error ? err.message : "followup_upsert failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="contact-activity-pane">
      {items.length ? (
        <ul className="followup-open-list">
          {items.map((item) => (
            <li key={item.followup_id}>
              <strong>{item.title}</strong>
              <div className="muted">
                {item.status}
                {item.due_at ? ` · ${formatNoteStamp(item.due_at)}` : ""}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No follow-ups for this person.</p>
      )}
      <label className="field">
        Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Coffee — NBAA Tue 10:00" />
      </label>
      <label className="field">
        Due / appointment
        <input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
      </label>
      <label className="field">
        Channel
        <select value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="email">email</option>
          <option value="teams">teams</option>
          <option value="linkedin">linkedin</option>
          <option value="call">call</option>
          <option value="in_person">in_person</option>
          <option value="other">other</option>
        </select>
      </label>
      <label className="field">
        Draft (not sent)
        <textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} />
      </label>
      {error ? <p className="err">{error}</p> : null}
      <button type="button" disabled={busy} onClick={() => void submit()}>
        {busy ? "Saving…" : "Save follow-up"}
      </button>
    </div>
  );
}
